import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, CalendarDays, KeyRound, LogOut } from "lucide-react";
import TripsView from "@/components/owner/TripsView";
import { OwnerHub } from "@/components/owner/OwnerHub";
import { ChangesPopup } from "@/components/owner/ChangesPopup";

export default function TripsPage() {
  const navigate = useNavigate();
  const token = localStorage.getItem("er-owner-token") || "";
  const wantsNew = new URLSearchParams(window.location.search).get("new") === "1";
  // on a phone, open on the big two-choice hub once per session — unless coming straight in for a new booking
  const [hubOpen, setHubOpen] = useState(
    () => window.matchMedia("(max-width: 640px)").matches && !sessionStorage.getItem("er-hub-seen") && !wantsNew
  );
  const [openNew, setOpenNew] = useState(wantsNew);

  const dismissHub = () => {
    sessionStorage.setItem("er-hub-seen", "1");
    setHubOpen(false);
  };

  if (!token) {
    return (
      <div data-testid="trips-locked" className="flex min-h-screen flex-col items-center justify-center bg-[#070709] px-5 text-center">
        <img src="/assets/logo.png" alt="Exclusive Reykjavik" className="h-16 w-auto mix-blend-screen" />
        <p className="mt-8 font-serif text-3xl font-light text-neutral-200">The private door is closed.</p>
        <p className="mt-3 max-w-sm text-sm text-neutral-500">Sign in at the concierge desk first — your key opens the trip builder too.</p>
        <Link
          to="/owner"
          data-testid="trips-goto-login"
          className="mt-8 flex items-center gap-2 bg-amber-500 px-8 py-3.5 text-xs font-semibold uppercase tracking-[0.25em] text-black transition-colors hover:bg-amber-300"
        >
          <KeyRound className="h-4 w-4" /> Go to Sign-In
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="trips-page" className="min-h-screen bg-[#070709] text-neutral-100">
      <ChangesPopup token={token} />
      <header className="sticky top-0 z-40 border-b border-amber-500/15 bg-[#0c0c10]/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-5 py-4">
          <div className="flex items-center gap-4">
            <img src="/assets/logo.png" alt="" className="h-10 w-auto mix-blend-screen" />
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-300/80">Concierge Desk</p>
              <h1 className="font-serif text-xl font-light text-neutral-50">Trip Builder</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/owner/calendar"
              data-testid="trips-goto-calendar"
              className="flex items-center gap-2 border border-neutral-700 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200"
            >
              <CalendarDays className="h-3.5 w-3.5" /> Calendar
            </Link>
            <Link
              to="/owner/invoices"
              data-testid="trips-goto-invoices"
              className="flex items-center gap-2 border border-neutral-700 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200"
            >
              Invoices
            </Link>
            <Link
              to="/owner/debts"
              data-testid="trips-goto-debts"
              className="flex items-center gap-2 border border-neutral-700 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200"
            >
              Debt List
            </Link>
            <Link
              to="/owner"
              data-testid="trips-back-to-requests"
              className="flex items-center gap-2 border border-neutral-700 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Requests
            </Link>
            <button
              data-testid="trips-logout-button"
              onClick={() => {
                localStorage.removeItem("er-owner-token");
                navigate("/owner");
              }}
              className="flex items-center gap-2 border border-neutral-700 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-red-400 hover:text-red-300"
            >
              <LogOut className="h-3.5 w-3.5" /> Sign Out
            </button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-5 py-10">
        {hubOpen ? (
          <OwnerHub
            onClients={dismissHub}
            onNew={() => {
              dismissHub();
              setOpenNew(true);
            }}
          />
        ) : (
          <TripsView token={token} openNewOnMount={openNew} />
        )}
      </main>
    </div>
  );
}
